// Components
import OrderSummaryTable from './OrderSummaryTable';
import OrderSummaryList from './OrderSummaryList';
import { Decimal } from 'decimal.js';
// Hooks
import { useCurrency } from '@/providers/CurrencyProvider';
import { useCartContext } from '@/providers/CartProvider';
import { useCheckoutContext } from '@/providers/CheckoutProvider';
import { useIsMobile } from '@/hooks/useIsMobile';

export default function OrderSummary({
  gasFee,
  gasFeeLoading,
  hideGasFee = false,
  invoiceDetails = null,
  isExactGas = false,
}) {
  const isMobile = useIsMobile(650);
  const {
    currency,
    handleConvertUsdToCoin,
    invoicePaymentSymbol,
    selectedCurrency,
    selectedPaymentFeeDisplayPrecision,
    selectedPaymentFeeSymbol,
    isCreditCardPayment,
  } = useCurrency();
  const {
    cartTotals: { cartSubtotal, cartQuantity },
    cartItems,
  } = useCartContext();
  const {
    couponState: { couponTotalUsd, couponTitles },
    appliedCoupons,
    checkoutState: { invoice },
  } = useCheckoutContext();
  const isCC = isCreditCardPayment;
  const displayPrecision = selectedCurrency.displayPrecision;
  const showGasFeeRow = !isCC && !hideGasFee;
  const hasInvoice = invoice['id'] !== undefined;

  const couponQty = hasInvoice
    ? invoice.invoicePrice.adjustments.length
    : appliedCoupons.length;

  const couponBalance = hasInvoice
    ? invoice.invoicePrice.adjustments.reduce((acc, adj) => acc - adj.amount.value, 0)
    : couponQty
      ? couponTotalUsd
      : 0;

  const couponTitleAndQty = `${couponTitles?.[0] ?? 'Coupon Discount'}${
    couponQty ? ` x ${couponQty}` : ''
  }`;

  const showCouponRow = hasInvoice
    ? invoice.invoicePrice.adjustments.length > 0
    : appliedCoupons?.length > 0;

  const subtotalUsd = hasInvoice
    ? Decimal.add(invoice.subtotalPriceUSD, couponBalance).toString()
    : cartSubtotal;

  const totalUsd = hasInvoice
    ? Decimal.add(invoice.totalPriceUSD, isExactGas ? 0 : gasFee?.usd || 0).toString()
    : Decimal.sub(
        Decimal.add(cartSubtotal, isCreditCardPayment ? 0 : gasFee?.usd || 0),
        couponBalance
      ).toString();
  const invoicePaymentTotal =
    hasInvoice && !isCC
      ? invoice.paymentTotals.find((total) => total.symbol === invoicePaymentSymbol)
      : null;
  const invoicePaymentTotalAmount = invoicePaymentTotal?.total;
  const hasInvoicePaymentTotal =
    !hasInvoice || isCC || invoicePaymentTotalAmount !== undefined;

  const subtotalCoin =
    hasInvoice && !isCC && hasInvoicePaymentTotal
      ? new Decimal(invoicePaymentTotalAmount).toFixed(displayPrecision)
      : handleConvertUsdToCoin(subtotalUsd);

  const totalCoin =
    hasInvoice && !isCC && hasInvoicePaymentTotal
      ? Decimal.add(
          invoicePaymentTotalAmount,
          isExactGas ? 0 : gasFee?.coin || 0
        ).toString()
      : handleConvertUsdToCoin(totalUsd);

  const totalQty = hasInvoice
    ? invoice.lineItems.reduce((acc, item) => acc + item.qty, 0)
    : cartQuantity;

  const totals = {
    subtotalUsd,
    subtotalCoin,
    totalUsd,
    totalCoin,
    totalQty,
  };

  const summaryProps = {
    couponBalance,
    couponTitleAndQty,
    currency,
    gasFee,
    gasFeeLoading,
    gasFeeToken: selectedPaymentFeeSymbol,
    gasFeePrecision: selectedPaymentFeeDisplayPrecision,
    isCC,
    displayPrecision,
    showGasFeeRow,
    showCouponRow,
    totals,
    handleConvertUsdToCoin,
    isExactGas,
    items: invoiceDetails?.invoiceItems ?? cartItems,
  };

  return (
    <>
      {isMobile ? (
        <OrderSummaryList {...summaryProps} />
      ) : (
        <OrderSummaryTable {...summaryProps} />
      )}
    </>
  );
}
